import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, X, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { encodeS3Url } from '@/components/encodeS3Url';

interface ImageGalleryProps {
  images: string[];
  title: string;
  qrCode?: string;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, title, qrCode }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const encodedImages = images.map(img => encodeS3Url(img));

  useEffect(() => {
    // Reset loading state when current image changes
    setIsLoading(true);
  }, [currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % encodedImages.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + encodedImages.length) % encodedImages.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Close fullscreen when clicking on the backdrop (not the image)
    if (e.target === e.currentTarget) {
      setIsFullscreen(false);
    }
  };

  if (isFullscreen) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
        onClick={handleBackdropClick}
      >
        {/* Close button */}
        <button
          onClick={toggleFullscreen}
          className="absolute top-4 right-4 bg-white/10 text-white rounded-full p-3 backdrop-blur-sm hover:bg-white/20 transition-all z-50"
          aria-label="Close fullscreen"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Image counter */}
        <div className="absolute top-4 left-4 bg-black/50 text-white px-4 py-2 rounded-full backdrop-blur-sm z-50">
          {currentIndex + 1} / {encodedImages.length}
        </div>

        {/* Main image container */}
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Main image */}
          <img
            src={encodedImages[currentIndex]}
            alt={`${title} - Image ${currentIndex + 1}`}
            className={`max-w-full max-h-full object-cover transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
            onLoad={handleImageLoad}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Navigation arrows */}
          {encodedImages.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 text-white rounded-full p-4 backdrop-blur-sm hover:bg-white/20 transition-all"
                aria-label="Previous image"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 text-white rounded-full p-4 backdrop-blur-sm hover:bg-white/20 transition-all"
                aria-label="Next image"
              >
                <ArrowRight className="h-6 w-6" />
              </button>
            </>
          )}
        </div>

        {/* Thumbnails */}
        {encodedImages.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90%] px-4 py-2 bg-black/30 rounded-lg backdrop-blur-sm">
            {encodedImages.map((image, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`relative flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${currentIndex === index ? 'border-gold scale-110' : 'border-white/30 hover:border-white/60'
                  }`}
                aria-label={`View image ${index + 1}`}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative overflow-hidden rounded-lg h-[85vh] bg-muted">
        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm z-10">
            <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Main image */}
        <img
          src={encodedImages[currentIndex]}
          alt={`${title} - Image ${currentIndex + 1}`}
          className={`w-full h-full object-cover transition-opacity duration-500 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          onLoad={handleImageLoad}
        />

        {/* Navigation arrows */}
        {encodedImages.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 text-white rounded-full p-2 backdrop-blur-sm hover:bg-black/50 transition-all"
              aria-label="Previous image"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 text-white rounded-full p-2 backdrop-blur-sm hover:bg-black/50 transition-all"
              aria-label="Next image"
            >
              <ArrowRight className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Fullscreen toggle */}
        <button
          onClick={toggleFullscreen}
          className="absolute top-4 right-4 bg-black/30 text-white rounded-full p-2 backdrop-blur-sm hover:bg-black/50 transition-all"
          aria-label="View fullscreen"
        >
          <Maximize2 className="h-5 w-5" />
        </button>

        {/* Image counter */}
        {encodedImages.length > 1 && (
          <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
            {currentIndex + 1} / {encodedImages.length}
          </div>
        )}
        {/* QR Code */}
        {qrCode && qrCode !== '/placeholder.svg' && (
          <div className="absolute bottom-4 right-4 bg-white/90 p-2 rounded-lg backdrop-blur-sm shadow-lg z-20 hidden md:block">
            <img
              src={qrCode}
              alt="Property QR Code"
              className="w-24 h-24 object-contain"
            />
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {encodedImages.length > 1 && (
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
          {encodedImages.map((image, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`relative flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all ${currentIndex === index ? 'border-gold' : 'border-transparent hover:border-gold/50'
                }`}
              aria-label={`View image ${index + 1}`}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
